<template>
  <div v-once id="visualization" ref="visualization"></div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { DataSet, DataView } from 'vis-data/peer'
import { Timeline } from 'vis-timeline/peer'
import { difference } from 'lodash'
import moment from 'moment'

interface Props {
  groups?: any[] | DataSet<any> | DataView<any>
  items?: any[] | DataSet<any> | DataView<any>
  options?: Record<string, any>
  range?: {
    start: number
    end: number
  }
  events?: string[]
  selection?: Array<any> | string
}

const props = withDefaults(defineProps<Props>(), {
  groups: () => [],
  items: () => [],
  options: () => ({}),
  range: () => ({
    start: moment().subtract(4, 'hours').valueOf(),
    end: moment().add(4, 'hours').valueOf(),
  }),
  events: () => ['rangechange', 'rangechanged', 'select'],
  selection: () => [],
})

const emit = defineEmits<{
  (e: string, props: any): void
}>()

const visualization = ref<HTMLDivElement>()
const timeline = ref<Timeline | null>(null)
const visItems = ref<DataSet<any> | null>(null)
const visGroups = ref<DataSet<any> | null>(null)

watch(
  () => props.groups,
  (newGroups) => {
    if (visGroups.value && Array.isArray(newGroups)) {
      const newIds = new DataSet(newGroups).getIds()
      const currentIds = visGroups.value.getIds()
      const toRemove = difference(currentIds, newIds)

      visGroups.value.update(newGroups)
      if (toRemove.length > 0) {
        visGroups.value.remove(toRemove)
      }
    }
  },
  { deep: true },
)

watch(
  () => props.items,
  (newItems) => {
    if (visItems.value && Array.isArray(newItems)) {
      const newIds = new DataSet(newItems).getIds()
      const currentIds = visItems.value.getIds()
      const toRemove = difference(currentIds, newIds)

      visItems.value.update(newItems)
      if (toRemove.length > 0) {
        visItems.value.remove(toRemove)
      }
    }
  },
  { deep: true },
)

watch(
  () => props.options,
  (newOptions) => {
    timeline.value?.setOptions(newOptions)
  },
  { deep: true },
)

watch(
  () => props.selection,
  (newSelection) => {
    timeline.value?.setSelection(newSelection)
  },
)

watch(
  () => props.range,
  (newRange) => {
    timeline.value?.setWindow(newRange.start, newRange.end)
  },
  { deep: true },
)

const setupEventListeners = () => {
  if (!timeline.value) return

  props.events.forEach((eventName) => {
    timeline.value?.on(eventName, (eventProps) => {
      const kebabEventName = eventName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()
      emit(kebabEventName, eventProps)
    })
  })
}

const removeEventListeners = () => {
  if (!timeline.value) return

  props.events.forEach((eventName) => {
    timeline.value?.off(eventName)
  })
}

onMounted(() => {
  if (!visualization.value) return

  visItems.value = new DataSet(Array.isArray(props.items) ? props.items : [])
  visGroups.value = new DataSet(Array.isArray(props.groups) ? props.groups : [])

  timeline.value = new Timeline(visualization.value, visItems.value, visGroups.value, {
    ...props.options,
    ...props.range,
  })

  setupEventListeners()
})

onBeforeUnmount(() => {
  removeEventListeners()
  timeline.value?.destroy()
  timeline.value = null
})
</script>

<style>
#visualization {
  height: 100%;
}

.vis-ltr .vis-label.vis-nested-group.vis-group-level-1,
.vis-ltr .vis-label.vis-nested-group.vis-group-level-1 .vis-inner {
  padding: 0;
  width: 100%;
}

.vis-item {
  background: none;
  border: 0;
}

.vis-item .vis-item-content {
  padding: 0;
  width: 100%;
}

.vis-item.completed {
  opacity: 0.6;
}

.vis-item.new {
  opacity: 0.8;
}

.vis-item.shadow {
  background: none;
}

.vis-item.shadow .vis-item-content {
  opacity: 0.5;
}

.vis-item.shadow .vis-item-overflow {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 14px;
  text-align: right;
  text-transform: uppercase;
}

.vis-item.shadow.overdue .vis-item-overflow {
  background: rgba(255, 205, 210, 0.6);
}

.vis-item.shadow.late .vis-item-overflow {
  background: rgba(255, 236, 179, 0.6);
}

.vis-item .vis-item-overflow {
  overflow: visible;
  min-height: 64px;
}
</style>
